<template>
  <div id="app" class="container-fluid">
    <!-- Header -->
    <full_eye_head :visit_id="visit_id" />

    <!-- Main Content - Eye Examination Module -->
    <div class="main-content">
      <div class="eye-tables-container">
        <!-- Right Eye (OD) Table -->
        <div class="eye-table-card od-table">
          <div class="card-header">
            <div class="header-content">
              <h4><i class="fas fa-eye right-eye-icon"></i> OD (Right Eye)</h4>
              <span class="last-update"
                >Updated: {{ formatDate(odLastUpdated) }}</span
              >
            </div>
            <button class="btn-edit" @click="editTable('OD')">
              <i class="fas fa-edit"></i> Edit OD
            </button>
          </div>
          <div class="card-body">
            <div class="table-wrapper">
              <table class="table eye-table">
                <thead>
                  <tr>
                    <th>Test Type</th>
                    <th>Result</th>
                    <th>Status</th>
                  </tr>
                </thead>
                <tbody>
                  <tr
                    v-for="test in odTests"
                    :key="test.id"
                    :class="getTestRowClass(test.result_status)"
                    @click="showTestDetail(test.id)"
                  >
                    <td class="test-name">
                      <i :class="getTestIcon(test.test_type)"></i>
                      <span class="test-name-text">{{ test.test_type }}</span>
                    </td>
                    <td class="test-value">{{ test.result_value || "---" }}</td>
                    <td>
                      <span
                        class="status-badge"
                        :class="getStatusClass(test.result_status)"
                      >
                        {{ getStatusLabel(test.result_status) }}
                      </span>
                    </td>
                  </tr>
                </tbody>
              </table>
            </div>
          </div>
        </div>

        <!-- Left Eye (OS) Table -->
        <div class="eye-table-card os-table">
          <div class="card-header">
            <div class="header-content">
              <h4><i class="fas fa-eye left-eye-icon"></i> OS (Left Eye)</h4>
              <span class="last-update"
                >Updated: {{ formatDate(osLastUpdated) }}</span
              >
            </div>
            <button class="btn-edit" @click="editTable('OS')">
              <i class="fas fa-edit"></i> Edit OS
            </button>
          </div>
          <div class="card-body">
            <div class="table-wrapper">
              <table class="table eye-table">
                <thead>
                  <tr>
                    <th>Test Type</th>
                    <th>Result</th>
                    <th>Status</th>
                  </tr>
                </thead>
                <tbody>
                  <tr
                    v-for="test in osTests"
                    :key="test.id"
                    :class="getTestRowClass(test.result_status)"
                    @click="showTestDetail(test.id)"
                  >
                    <td class="test-name">
                      <i :class="getTestIcon(test.test_type)"></i>
                      <span class="test-name-text">{{ test.test_type }}</span>
                    </td>
                    <td class="test-value">{{ test.result_value || "---" }}</td>
                    <td>
                      <span
                        class="status-badge"
                        :class="getStatusClass(test.result_status)"
                      >
                        {{ getStatusLabel(test.result_status) }}
                      </span>
                    </td>
                  </tr>
                </tbody>
              </table>
            </div>
          </div>
        </div>

        <!-- Both Eyes (OU) Table - Full width at bottom -->
        <div class="eye-table-card ou-table full-width">
          <div class="card-header">
            <div class="header-content">
              <h4><i class="fas fa-glasses"></i> OU (Both Eyes)</h4>
              <span class="last-update"
                >Updated: {{ formatDate(ouLastUpdated) }}</span
              >
            </div>
            <button class="btn-edit" @click="editTable('OU')">
              <i class="fas fa-edit"></i> Edit OU
            </button>
          </div>
          <div class="card-body">
            <div class="table-wrapper">
              <table class="table eye-table">
                <thead>
                  <tr>
                    <th>Test Type</th>
                    <th>Result</th>
                    <th>Status</th>
                  </tr>
                </thead>
                <tbody>
                  <tr
                    v-for="test in ouTests"
                    :key="test.id"
                    :class="getTestRowClass(test.result_status)"
                    @click="showTestDetail(test.id)"
                  >
                    <td class="test-name">
                      <i :class="getTestIcon(test.test_type)"></i>
                      <span class="test-name-text">{{ test.test_type }}</span>
                    </td>
                    <td class="test-value">{{ test.result_value || "---" }}</td>
                    <td>
                      <span
                        class="status-badge"
                        :class="getStatusClass(test.result_status)"
                      >
                        {{ getStatusLabel(test.result_status) }}
                      </span>
                    </td>
                  </tr>
                </tbody>
              </table>
            </div>
          </div>
        </div>
      </div>

      <!-- Summary Card -->
      <div class="summary-section">
        <div class="summary-card">
          <div class="card-header">
            <h5><i class="fas fa-clipboard-check"></i> Examination Summary</h5>
            <button class="btn-summary-edit" @click="editSummary">
              <i class="fas fa-edit"></i> Edit Summary
            </button>
          </div>
          <div class="card-body">
            <div class="summary-content">
              <div class="summary-item">
                <div class="summary-label">
                  <i class="fas fa-eye right-eye-icon"></i> OD Diagnosis
                </div>
                <div class="summary-value">
                  Mild Myopia (-1.25D) with Normal IOP (16mmHg)
                </div>
              </div>
              <div class="summary-item">
                <div class="summary-label">
                  <i class="fas fa-eye left-eye-icon"></i> OS Diagnosis
                </div>
                <div class="summary-value">
                  Moderate Myopia (-2.50D) with Elevated IOP (22mmHg)
                </div>
              </div>
              <div class="summary-item">
                <div class="summary-label">
                  <i class="fas fa-glasses"></i> Recommendation
                </div>
                <div class="summary-value">
                  Prescription glasses required, monitor IOP in OS, follow-up in
                  3 months
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>

  <!-- Modal Components -->
  <!-- <individual_eye_test_input
    :testId="test_id_current"
    :visit_id="visit_id"
    @close-test-input="close_test_input_modal"
    v-if="show_individual_test_input"
  /> -->
</template>

<script>
// import individual_eye_test_input from "./individual_eye_test_input.vue";
import full_eye_head from "./full_eye_head.vue";

export default {
  name: "EyeExamination",
  data() {
    return {
      show_individual_test_input: false,
      visit_id: this.$route.params.visit_id,
      test_id_current: "",
      odLastUpdated: new Date(),
      osLastUpdated: new Date("2024-01-10"),
      ouLastUpdated: new Date("2024-01-09"),
      eye_exams: [
        // Right Eye (OD) tests
        {
          id: 1,
          eye: "OD",
          test_type: "Distance Vision",
          result_value: "20/20",
          result_status: "normal",
        },
        {
          id: 2,
          eye: "OD",
          test_type: "Near Vision",
          result_value: "N6",
          result_status: "normal",
        },
        {
          id: 3,
          eye: "OD",
          test_type: "Color Vision",
          result_value: "14/14",
          result_status: "normal",
        },
        {
          id: 4,
          eye: "OD",
          test_type: "Depth Perception",
          result_value: "40 sec",
          result_status: "normal",
        },
        {
          id: 5,
          eye: "OD",
          test_type: "Intraocular Pressure",
          result_value: "16 mmHg",
          result_status: "normal",
        },
        {
          id: 6,
          eye: "OD",
          test_type: "Refraction (Sphere)",
          result_value: "-1.25 D",
          result_status: "myopia",
        },
        {
          id: 7,
          eye: "OD",
          test_type: "Refraction (Cylinder)",
          result_value: "-0.50 D",
          result_status: "astigmatism",
        },
        {
          id: 8,
          eye: "OD",
          test_type: "Visual Field",
          result_value: "Full",
          result_status: "normal",
        },

        // Left Eye (OS) tests
        {
          id: 9,
          eye: "OS",
          test_type: "Distance Vision",
          result_value: "20/40",
          result_status: "reduced",
        },
        {
          id: 10,
          eye: "OS",
          test_type: "Near Vision",
          result_value: "N8",
          result_status: "reduced",
        },
        {
          id: 11,
          eye: "OS",
          test_type: "Color Vision",
          result_value: "12/14",
          result_status: "deficit",
        },
        {
          id: 12,
          eye: "OS",
          test_type: "Depth Perception",
          result_value: "60 sec",
          result_status: "reduced",
        },
        {
          id: 13,
          eye: "OS",
          test_type: "Intraocular Pressure",
          result_value: "22 mmHg",
          result_status: "elevated",
        },
        {
          id: 14,
          eye: "OS",
          test_type: "Refraction (Sphere)",
          result_value: "-2.50 D",
          result_status: "myopia",
        },
        {
          id: 15,
          eye: "OS",
          test_type: "Refraction (Cylinder)",
          result_value: "-1.25 D",
          result_status: "astigmatism",
        },
        {
          id: 16,
          eye: "OS",
          test_type: "Visual Field",
          result_value: "Peripheral Defect",
          result_status: "abnormal",
        },

        // Both Eyes (OU) tests
        {
          id: 17,
          eye: "OU",
          test_type: "Binocular Vision",
          result_value: "Good",
          result_status: "normal",
        },
        {
          id: 18,
          eye: "OU",
          test_type: "Stereoacuity",
          result_value: "40 arcsec",
          result_status: "normal",
        },
        {
          id: 19,
          eye: "OU",
          test_type: "Convergence",
          result_value: "8 cm",
          result_status: "normal",
        },
        {
          id: 20,
          eye: "OU",
          test_type: "Accommodation",
          result_value: "5 D",
          result_status: "reduced",
        },
        {
          id: 21,
          eye: "OU",
          test_type: "Pupil Reaction",
          result_value: "Brisk",
          result_status: "normal",
        },
        {
          id: 22,
          eye: "OU",
          test_type: "Contrast Sensitivity",
          result_value: "1.8 log",
          result_status: "normal",
        },
      ],
    };
  },
  computed: {
    odTests() {
      return this.eye_exams.filter((test) => test.eye === "OD");
    },
    osTests() {
      return this.eye_exams.filter((test) => test.eye === "OS");
    },
    ouTests() {
      return this.eye_exams.filter((test) => test.eye === "OU");
    },
  },
  components: {
    full_eye_head,
    // individual_eye_test_input
  },
  methods: {
    close_test_input_modal() {
      this.test_id_current = "";
      this.show_individual_test_input = false;
    },

    showTestDetail(testId) {
      this.test_id_current = testId;
      this.show_individual_test_input = true;
    },

    editTable(eye) {
      console.log(`Editing ${eye} table`);
      const tests = this.eye_exams.filter((test) => test.eye === eye);
      if (tests.length > 0) {
        this.showTestDetail(tests[0].id);
      }
    },

    editSummary() {
      console.log("Editing examination summary");
      // Implement summary editing logic
    },

    getTestRowClass(status) {
      const classMap = {
        normal: "test-row-normal",
        reduced: "test-row-reduced",
        elevated: "test-row-elevated",
        myopia: "test-row-myopia",
        astigmatism: "test-row-astigmatism",
        deficit: "test-row-deficit",
        abnormal: "test-row-abnormal",
      };
      return classMap[status] || "";
    },

    getStatusClass(status) {
      const classMap = {
        normal: "status-normal",
        reduced: "status-reduced",
        elevated: "status-elevated",
        myopia: "status-myopia",
        astigmatism: "status-astigmatism",
        deficit: "status-deficit",
        abnormal: "status-abnormal",
      };
      return classMap[status] || "status-pending";
    },

    getStatusLabel(status) {
      const labelMap = {
        normal: "Normal",
        reduced: "Reduced",
        elevated: "Elevated",
        myopia: "Myopia",
        astigmatism: "Astigmatism",
        deficit: "Deficit",
        abnormal: "Abnormal",
      };
      return labelMap[status] || "Pending";
    },

    getTestIcon(testType) {
      const iconMap = {
        "Distance Vision": "fas fa-chart-line",
        "Near Vision": "fas fa-search",
        "Color Vision": "fas fa-palette",
        "Depth Perception": "fas fa-layer-group",
        "Intraocular Pressure": "fas fa-tachometer-alt",
        Refraction: "fas fa-ruler-combined",
        "Visual Field": "fas fa-expand-alt",
        "Binocular Vision": "fas fa-eye",
        Stereoacuity: "fas fa-cube",
        Convergence: "fas fa-arrows-alt-h",
        Accommodation: "fas fa-adjust",
        "Pupil Reaction": "fas fa-circle",
        "Contrast Sensitivity": "fas fa-sliders-h",
      };
      return iconMap[testType] || "fas fa-stethoscope";
    },

    formatDate(date) {
      return new Date(date).toLocaleDateString("en-US", {
        month: "short",
        day: "numeric",
        year: "numeric",
      });
    },
  },
  mounted() {
    console.log("Eye examination module loaded with dummy data");
  },
};
</script>

<style scoped>
.eye-tables-container {
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 20px;
  margin-bottom: 20px;
}

.eye-table-card {
  background: white;
  border-radius: 12px;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.08);
  transition: all 0.3s ease;
  display: flex;
  flex-direction: column;
  overflow: hidden;
}

.eye-table-card.full-width {
  grid-column: 1 / -1; /* Spans all columns */
  margin-top: 10px;
}

.eye-table-card:hover {
  transform: translateY(-2px);
  box-shadow: 0 8px 20px rgba(0, 0, 0, 0.12);
}

.od-table {
  border-top: 4px solid #2196f3;
}

.os-table {
  border-top: 4px solid #4caf50;
}

.ou-table {
  border-top: 4px solid #ff9800;
}

.card-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 15px 20px;
  background: #f8f9fa;
  border-bottom: 1px solid #e9ecef;
}

.header-content {
  flex: 1;
}

.card-header h4 {
  margin: 0 0 5px 0;
  font-weight: 600;
  color: #2c3e50;
  display: flex;
  align-items: center;
  gap: 10px;
}

.last-update {
  font-size: 0.8rem;
  color: #6c757d;
}

.right-eye-icon {
  color: #2196f3;
}

.left-eye-icon {
  color: #4caf50;
}

.btn-edit {
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  color: white;
  border: none;
  padding: 8px 16px;
  border-radius: 6px;
  font-size: 0.85rem;
  font-weight: 500;
  cursor: pointer;
  display: flex;
  align-items: center;
  gap: 6px;
  transition: all 0.3s ease;
  white-space: nowrap;
  margin-left: 15px;
}

.btn-edit:hover {
  transform: scale(1.05);
  box-shadow: 0 4px 12px rgba(102, 126, 234, 0.3);
}

.card-body {
  padding: 0;
  flex: 1;
  display: flex;
  flex-direction: column;
}

.table-wrapper {
  flex: 1;
  display: flex;
  flex-direction: column;
}

.eye-table {
  margin: 0;
  width: 100%;
  font-size: 0.9rem;
  border-collapse: separate;
  border-spacing: 0;
  table-layout: fixed; /* Ensures consistent column widths */
}

.eye-table thead th {
  background-color: #f1f3f4;
  border-bottom: 2px solid #dee2e6;
  font-weight: 600;
  color: #495057;
  padding: 12px 15px;
  position: sticky;
  top: 0;
  z-index: 10;
}

.eye-table tbody tr {
  cursor: pointer;
  transition: background-color 0.2s ease;
  border-bottom: 1px solid #f1f3f4;
}

.eye-table tbody tr:last-child {
  border-bottom: none;
}

.eye-table tbody tr:hover {
  background-color: #f8f9fa;
}

.eye-table td {
  padding: 12px 15px;
  vertical-align: middle;
  overflow: hidden;
  text-overflow: ellipsis;
}

.test-name {
  display: flex;
  align-items: center;
  gap: 10px;
  font-weight: 500;
  color: #2c3e50;
  min-width: 0; /* Allows proper text truncation */
}

.test-name i {
  color: #6c757d;
  font-size: 0.9rem;
  flex-shrink: 0;
}

.test-name-text {
  flex: 1;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.test-value {
  font-weight: 600;
  color: #2c3e50;
  font-family: "Courier New", monospace;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.status-badge {
  padding: 4px 10px;
  border-radius: 12px;
  font-size: 0.75rem;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  white-space: nowrap;
  display: inline-block;
  overflow: hidden;
  text-overflow: ellipsis;
}

.status-normal {
  background-color: #e8f5e9;
  color: #2e7d32;
}

.status-reduced {
  background-color: #fff3e0;
  color: #f57c00;
}

.status-elevated {
  background-color: #ffebee;
  color: #c62828;
}

.status-myopia {
  background-color: #e3f2fd;
  color: #1565c0;
}

.status-astigmatism {
  background-color: #f3e5f5;
  color: #7b1fa2;
}

.status-deficit {
  background-color: #f5f5f5;
  color: #616161;
  border: 1px solid #e0e0e0;
}

.status-abnormal {
  background-color: #ffebee;
  color: #c62828;
}

/* Summary Section */
.summary-section {
  margin-top: 30px;
}

.summary-card {
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  border-radius: 12px;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
  color: white;
}

.summary-card .card-header {
  background: rgba(255, 255, 255, 0.1);
  border-bottom: 1px solid rgba(255, 255, 255, 0.2);
  display: flex;
  justify-content: space-between;
  align-items: center;
}

.summary-card .card-header h5 {
  color: white;
  margin: 0;
  display: flex;
  align-items: center;
  gap: 10px;
}

.btn-summary-edit {
  background: rgba(255, 255, 255, 0.2);
  color: white;
  border: 1px solid rgba(255, 255, 255, 0.3);
  padding: 6px 12px;
  border-radius: 6px;
  font-size: 0.85rem;
  cursor: pointer;
  display: flex;
  align-items: center;
  gap: 6px;
  transition: all 0.3s ease;
}

.btn-summary-edit:hover {
  background: rgba(255, 255, 255, 0.3);
}

.summary-card .card-body {
  padding: 20px;
}

.summary-content {
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 20px;
}

.summary-item {
  text-align: left;
}

.summary-label {
  font-size: 0.9rem;
  opacity: 0.9;
  margin-bottom: 8px;
  display: flex;
  align-items: center;
  gap: 8px;
}

.summary-value {
  font-size: 1rem;
  font-weight: 600;
  line-height: 1.4;
}

/* Responsive Design */
@media (max-width: 1200px) {
  .eye-tables-container {
    grid-template-columns: repeat(2, 1fr);
  }

  .summary-content {
    grid-template-columns: repeat(3, 1fr);
  }
}

/* Medium screens: Keep 2 tables per row for OD/OS, OU full width */
@media (max-width: 992px) {
  .eye-tables-container {
    grid-template-columns: repeat(2, 1fr);
  }

  .summary-content {
    grid-template-columns: repeat(2, 1fr);
  }

  .summary-item:last-child {
    grid-column: span 2;
  }
}

/* Small screens: Stack OD and OS vertically, OU full width */
@media (max-width: 768px) {
  .eye-tables-container {
    grid-template-columns: 1fr;
  }

  .summary-content {
    grid-template-columns: 1fr;
    gap: 15px;
  }

  .summary-item:last-child {
    grid-column: span 1;
  }

  .card-header {
    flex-direction: column;
    align-items: flex-start;
    gap: 10px;
  }

  .btn-edit {
    align-self: flex-start;
    margin-left: 0;
  }
}

/* Extra small screens: Enable horizontal scroll ONLY when necessary */
@media (max-width: 576px) {
  .table-wrapper {
    overflow-x: auto;
    -webkit-overflow-scrolling: touch;
  }

  .eye-table {
    min-width: 550px; /* Ensure table has minimum width before scrolling */
    table-layout: auto; /* Allow flexible column widths */
  }

  .card-header h4 {
    font-size: 1.1rem;
  }

  .btn-edit {
    padding: 6px 12px;
    font-size: 0.8rem;
  }

  .test-name-text {
    min-width: 120px;
  }

  .summary-content {
    grid-template-columns: 1fr;
  }
}

/* Very small screens */
@media (max-width: 400px) {
  .eye-table {
    min-width: 400px;
  }

  .card-header {
    padding: 12px;
  }

  .summary-card .card-body {
    padding: 15px;
  }
}

/* Print styles */
@media print {
  .eye-tables-container {
    display: block;
  }

  .eye-table-card {
    break-inside: avoid;
    margin-bottom: 20px;
    box-shadow: none;
    border: 1px solid #ddd;
  }

  .eye-table-card.full-width {
    width: 100%;
  }

  .btn-edit,
  .btn-summary-edit {
    display: none;
  }
}
</style>
